import { CheckCircle2, Pencil, Save, X, RotateCcw, Trash2 } from "lucide-react";
import ArrowDisplay from "@/components/ArrowDisplay";
import DirectionPad from "./DirectionPad";
import CategoryEditor from "./CategoryEditor";
import { formatDate } from "./useReviewData";

const StratagemCard = ({
  item,
  isEditing,
  editingValues,
  setEditingValues,
  categoryInput,
  setCategoryInput,
  beginEdit,
  cancelEdit,
  saveOverride,
  deleteStratagem,
  undoStratagem,
  addDirection,
  removeLastDirection,
  clearDirections,
  addCategory,
  removeCategory,
}) => (
  <div className="hud-card clip-corners p-5" data-testid={`review-card-${item.id}`}>
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <div className="flex items-center gap-2">
          <p className="text-xs uppercase tracking-[0.3em] text-gray-500">{item.category}</p>
          {item.is_new && (
            <span className="px-2 py-0.5 text-[10px] font-bold bg-green-500 text-black rounded">NEW</span>
          )}
        </div>
        <h3 className="mt-2 font-headings text-2xl uppercase tracking-wide text-white">{item.name}</h3>
        <div className="mt-2 flex items-center gap-3 text-xs">
          <CheckCircle2 className="h-4 w-4 text-green-400" />
          <span className="text-gray-300">Source: {item.source || "GitHub"}</span>
          {item.manual_override && <span className="uppercase tracking-[0.2em] text-yellow-400">Override</span>}
          {item.auto_approved && <span className="uppercase tracking-[0.2em] text-blue-400">Auto-approved</span>}
        </div>
        {item.release_date && (
          <p className="mt-1 text-xs text-gray-500">
            Released: {formatDate(item.release_date)}
            {item.new_until_date && ` • New until: ${formatDate(item.new_until_date)}`}
          </p>
        )}
        {item.categories && item.categories.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {item.categories.map((cat) => (
              <span key={cat} className="border border-white/20 px-2 py-0.5 text-xs text-gray-300">{cat}</span>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {!isEditing ? (
          <button
            onClick={() => beginEdit(item)}
            className="flex items-center gap-2 border border-white/20 px-3 py-2 text-xs font-data text-white"
            data-testid={`review-edit-${item.id}`}
          >
            <Pencil className="h-4 w-4" />
            Edit
          </button>
        ) : (
          <button
            onClick={() => saveOverride(item)}
            className="flex items-center gap-2 bg-[#FFE800] px-3 py-2 text-xs font-data text-black"
            data-testid={`review-save-${item.id}`}
          >
            <Save className="h-4 w-4" />
            Save
          </button>
        )}
        {isEditing ? (
          <button
            onClick={cancelEdit}
            className="flex items-center gap-2 border border-white/10 px-3 py-2 text-xs font-data text-gray-300"
            data-testid={`review-cancel-${item.id}`}
          >
            <X className="h-4 w-4" />
            Cancel
          </button>
        ) : (
          <>
            <button
              onClick={() => undoStratagem(item.id, item.name)}
              className="flex items-center gap-2 border border-white/10 px-3 py-2 text-xs font-data text-gray-300"
              data-testid={`review-undo-${item.id}`}
            >
              <RotateCcw className="h-4 w-4" />
              Undo
            </button>
            <button
              onClick={() => deleteStratagem(item)}
              className="flex items-center gap-2 border border-red-500/40 px-3 py-2 text-xs font-data text-red-300"
              data-testid={`review-delete-${item.id}`}
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </button>
          </>
        )}
      </div>
    </div>

    <div className="mt-4">
      {isEditing ? (
        <div className="space-y-4">
          <div className="grid gap-3 md:grid-cols-2">
            <input
              value={editingValues.name}
              onChange={(e) => setEditingValues((p) => ({ ...p, name: e.target.value }))}
              className="w-full bg-black/50 border border-white/10 text-white font-data text-sm py-2 px-3"
              placeholder="Name"
            />
            <input
              value={editingValues.category}
              onChange={(e) => setEditingValues((p) => ({ ...p, category: e.target.value }))}
              className="w-full bg-black/50 border border-white/10 text-white font-data text-sm py-2 px-3"
              placeholder="Primary Category"
            />
          </div>
          <CategoryEditor
            categories={editingValues.categories}
            inputValue={categoryInput}
            onInputChange={setCategoryInput}
            onAdd={addCategory}
            onRemove={removeCategory}
          />
          <div className="space-y-3">
            <p className="text-xs text-gray-500">Code</p>
            <ArrowDisplay sequence={editingValues.code} size="sm" testIdPrefix={`review-edit-${item.id}`} />
            <DirectionPad
              onDirection={addDirection}
              onBackspace={removeLastDirection}
              onClear={clearDirections}
            />
          </div>
        </div>
      ) : (
        <ArrowDisplay sequence={item.code} size="sm" testIdPrefix={`review-${item.id}`} />
      )}
    </div>
  </div>
);

export default StratagemCard;
